"use client";

import { useDataLayerContext } from "@/datalayer/DataLayerContextProvider";
import { useCarouselVisibility } from "@/hooks/useCarouselVisibility";
import useRegionAndLocale from "@/hooks/useRegionAndLocale";
import { ProductData } from "@/lib/5874/types";
import { ProductsViewedTrigger } from "@/lib/_event-bus/components/ProductsViewedTrigger";
import { BigCommerceStoreContext } from "@/providers/BigCommerceStoreProvider";
import clsx from "clsx";
import React, { useContext, useId, useState } from "react";
import SwiperType from "swiper";
import { Navigation, Scrollbar } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import ProductCard from "../ProductCard/ProductCard";
import SwiperNavigationButtons from "../SwiperNavigationButton/SwiperNavigationButton";
import ProductSliderSkeleton, {
	ProductSliderSkeletonSmall,
} from "../UI/Skeleton/ProductSliderSkeleton";
import RecommendationCard from "./components/RecommendationCard";
import { useCarouselCart } from "./hooks/useCarouselCart";
import styles from "./ProductRecommendationCarousel.module.scss";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/scrollbar";

interface DisplayOptions {
	displayType: "cards" | "recommendations";
	showProductForm?: boolean;
	showAvailableProductsOnly?: boolean;
}

export interface ProductRecommendationCarouselProps {
	title: string;
	graphqlId: string;
	useProductCards?: boolean;
	products?: ProductData[];
	placementProductId: string;
	isLoading: boolean;
	showAllProducts?: boolean;
	displayOptions?: DisplayOptions;
}

const ProductRecommendationCarousel: React.FC<
	ProductRecommendationCarouselProps
> = ({
	products,
	title,
	useProductCards,
	graphqlId,
	placementProductId,
	isLoading = false,
	showAllProducts = false,
	displayOptions,
	...props
}) => {
	const mergedDisplayOptions = {
		displayType:
			displayOptions !== undefined
				? displayOptions.displayType
				: useProductCards
				  ? "cards"
				  : "recommendations",
		showAvailableProductsOnly:
			displayOptions !== undefined
				? displayOptions.showAvailableProductsOnly
				: !showAllProducts,
		showProductForm:
			displayOptions !== undefined ? displayOptions.showProductForm : false,
	};

	const { region, lang } = useRegionAndLocale();
	const carouselId = useId();
	const { currencyFormattingRules } = useContext(BigCommerceStoreContext);
	const [swiper, setSwiper] = useState<SwiperType | undefined>();

	const { productAddedToCart, addToCartHandler, isModifyingCart } =
		useCarouselCart();
	const { selectItem } = useDataLayerContext();

	const { visibleProducts, updateVisibleProducts } = useCarouselVisibility({
		products,
		swiper,
		isLoading,
		itemListId: graphqlId,
		itemListName: title,
		currency: currencyFormattingRules.currency,
	});

	const handleSlideChangeTransitionEnd = () => {
		updateVisibleProducts();
	};

	const skeletonContent =
		mergedDisplayOptions.displayType === "cards"
			? ProductSliderSkeleton
			: ProductSliderSkeletonSmall;

	const showSkeleton = isLoading && (!products || products.length === 0);

	const content = (
		<>
			<ProductsViewedTrigger
				products={visibleProducts}
				eventName={
					(props as any)["data-eventname"] || "Product Carousel Products Viewed"
				}
			/>
			<h2 className={styles.title}>{title}</h2>
			<Swiper
				modules={[Scrollbar, Navigation]}
				onSwiper={setSwiper}
				onSlideChangeTransitionEnd={handleSlideChangeTransitionEnd}
				scrollbar
				noSwiping
				noSwipingSelector='a'
				preventClicks={false}
				preventClicksPropagation={false}
				watchSlidesProgress={true}
				navigation={{
					enabled: true,
					nextEl: `[data-button-ref="${carouselId}-button-next"]`,
					prevEl: `[data-button-ref="${carouselId}-button-previous"]`,
				}}
				spaceBetween={showSkeleton ? 16 : 0}
				breakpoints={{
					1000: {
						slidesPerView: mergedDisplayOptions.displayType === "cards" ? 4 : 2,
					},
					700: {
						slidesPerView: mergedDisplayOptions.displayType === "cards" ? 2 : 2,
					},
					551: {
						slidesPerView: 1,
					},
				}}
			>
				{showSkeleton
					? skeletonContent
					: (products ?? []).map((product, index: number) => {
							return (
								<SwiperSlide
									key={`${product.id}-${index}`}
									onClick={() => {
										selectItem(
											product,
											product.id,
											carouselId,
											product?.categories[0]?.name ?? carouselId,
											currencyFormattingRules.currency,
										);
									}}
									data-position={index}
									data-tracked={false}
									className={styles.slide}
								>
									{mergedDisplayOptions.displayType === "cards" ? (
										<>
											<ProductCard
												product={product}
												showSimpleView={true}
												className={styles.productCardOverrides}
												currencyCode={currencyFormattingRules.currency}
												itemListId={graphqlId}
												itemListName={title}
												isInRecommendationCarousel={
													!mergedDisplayOptions.showProductForm
												}
												isLoading={isLoading}
												hidePrice={
													product.productClass?.toLowerCase() === "parent"
												}
											/>
										</>
									) : (
										<RecommendationCard
											product={product}
											isLoading={isLoading}
											region={region}
											lang={lang}
											isAddedToCart={productAddedToCart === product.id}
											isModifyingCart={isModifyingCart}
											onAddToCart={() => addToCartHandler(product)}
										/>
									)}
								</SwiperSlide>
							);
					  })}
				<SwiperNavigationButtons buttonIdentifier={carouselId} />
			</Swiper>
		</>
	);

	return (
		<section
			className={clsx([
				styles.productRecommendations,
				mergedDisplayOptions.displayType === "cards" ? styles.fullSize : "",
			])}
		>
			{mergedDisplayOptions.displayType === "cards" ? (
				<div className={styles.inner}>{content}</div>
			) : (
				content
			)}
		</section>
	);
};

export default ProductRecommendationCarousel;
